import { type TwoFactorAuthType } from '@pkg/types';

import { type ArtifactBreakdownResponse } from '@app/store/types/artifacts';
import { type FeatureFlags } from '@app/store/types/features';

import { type Order } from '../../../resources/nft/types';
import { type EmojiId, type ExtendedEmoji, type TransfersResponse } from '../emoji';
import { type ResourceId } from '../general';
import { type PaginatedResult, type PagingParams } from '../requests';

import { type OrgRoles, type Roles, type Scopes } from './roles';
import { type UserData } from './user-data';

interface Wallet {
    eth_address: string;
    user_id: string;
}

export interface TwitterData {
    created_at: string;
    id: string;
    key: string;
    updated_at: string;
    user_id: string;
    value: {
        email: string;
        id: string;
        screen_name: string;
    };
}
export interface InitializeUser {
    artifact_breakdown: ArtifactBreakdownResponse;
    current_user: UserAccount;
    extended_list: ExtendedEmoji[];
    editions: number[];
    everflow_transaction_id: string | null;
    frontend_feature_flags?: UserData;
    incoming_transfers: TransfersResponse[];
    liquid_id: ResourceId;
    outgoing_transfers: PaginatedResult<TransfersResponse>;
    sidebar?: UserData;
    twitter_data: null | TwitterData;
    wallets: Wallet[];
    websocket_auth_token: string;
    opt_ins: OptIn[];
    // TODO - align these types: UserAccount & InitializeUser?
}

export interface UserAccount {
    user: User;
    role: Roles;
    frontend_feature_flags?: FeatureFlags;
    global_scopes?: Scopes[] | null;
    organization_roles: OrganizationRoles;
    organization_scopes: OrganizationScopes;
    everflow_transaction_id: string | null;
    twitter_data?: null | TwitterData;
    wallets: Wallet[];
    websocket_auth_token: string;
}
export interface User {
    id: string;
    email: string;
    email_verified_at?: null;
    first_name?: null;
    last_name?: null;
    eid?: string;
    eid_status?: string;
    role?: Roles;
    created_at: string;
    updated_at: string;
    two_factor_auth?: null | TwoFactorAuthType[];
    two_factor_should_prompt: boolean;
    username?: string;
}
export interface OrganizationRoles {
    [org_id: string]: OrgRoles;
}
export interface OrganizationScopes {
    [org_id: string]: Scopes[] | null;
}

export interface DisplayUser extends User {
    alternate_id?: string;
    free_limit?: number;
    remaining_free_emoji?: number;
    is_active?: boolean;
    pubkeys?: string[];
}

export interface DisplayUserExtended extends DisplayUser {
    emoji_ids: EmojiId[];
    inviter_email?: string;
    invite_limit?: number;
    // todo get more invite info from BE
}

export interface UpdateUserParameters
    extends Omit<
        DisplayUser,
        'created_at' | 'updated_at' | 'two_factor_should_prompt' | 'id' | 'email_verified_at' | 'email'
    > {
    email?: string;
    subscribed?: boolean;
    password?: string;
    current_password?: string;
    confirm_eid?: boolean;
}

export type UpdateUsernameParams = { username: string };
export interface AdminUpdateUser extends UpdateUserParameters {
    user_parameters?: UpdateUserParameters;
    eid_status?: string;
}

export enum OptInType {
    FantasyGameInvites = 'FantasyGameInvites',
    FantasyGameOverAnnouncements = 'FantasyGameOverAnnouncements',
    MarketingList = 'MarketingList',
    FantasyDailyLineupSummary = 'FantasyDailyLineupSummary',
}

export interface OptinUser {
    opted_in: boolean;
}

export interface RegisterUser {
    email?: string;
    password?: string;
    first_name?: string;
    last_name?: string;
    partner_conversion_id: string;
}

export interface ConsumeUser {
    userId: string;
}
export interface MyOrdersResponse {
    data: Order[];
    paging: {
        dir: 'ASC' | 'DESC';
        limit: number;
        page: number;
        sort: string;
        tags?: Record<string, string>;
        total: number;
    };
}

export interface OptIn {
    name: string;
    opt_in_type: OptInType;
    required: boolean;
    opted_in: boolean;
}

export interface DiscordUserResponse {
    id: string;
    key: string;
    user_id: string;
    value: {
        id: string;
        discriminator: string;
        email: string;
        username: string;
    };
    created_at: Date;
    updated_at: Date;
}

export interface TwitterUserResponse {
    id: string;
    key: string;
    user_id: string;
    value: {
        id: string;
        email: string;
        screen_name: string;
    };
    created_at: Date;
    updated_at: Date;
}

export interface ListUsersResponseUser {
    id: string;
    primary_yat: string | null;
    // TODO we should only have `twitter` or `twitter_handle` after BUG-660
    twitter: string | null;
    twitter_handle: string | null;
}

export interface ListUsersResponse {
    data: ListUsersResponseUser[];
    paging: PagingParams;
}

export interface PrimaryYatParams {
    eid: EmojiId;
    confirm_eid: boolean;
}
